// src/shared/useStreak.ts
// ─────────────────────────────────────────────────────────────────────────
// Lightweight, web-side streak READ for the nav + gem popover.
//
// SCOPE (deliberate): this is NOT a streak service. It reads the streak state
// that the learning layer already persists and derives a display status —
// including the freeze-aware 'frozen' status. No calendar history, no
// recovery, no server validation.
//
// SOURCE OF TRUTH: `nis.gamification.streak`, written by recordStudyToday()
// in lib/srs.ts. The tick now lives in the reward dispatcher
// (lib/rewards/rewards.ts): any meaningful LEARNING action counts — lessons,
// quizzes, reviews — never mere presence. Streak Freezes are consumed inside
// core applyStudyToday; this hook only READS freezesOwned to derive whether
// a lapsed streak is currently protected ('frozen') or actually broken.
//
// Writes live with the writer (srs.ts → recordStudyToday / addStreakFreeze).
// This file is read-only on purpose; there is no second writer to drift out
// of sync.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { streakDayISO } from '@nisk/core/srs';

// Must match lib/srs.ts STREAK_KEY (and migrateLocalStorage.ts) exactly.
const STREAK_KEY = 'nis.gamification.streak';

export type StreakStatus =
	| 'active-today' // studied today — streak protected
	| 'at-risk' // studied yesterday, not yet today
	| 'frozen' // missed day(s), fully covered by owned Streak Freezes
	| 'broken' // last study older than the freezes can bridge
	| 'none'; // never studied

export interface StreakView {
	currentStreak: number;
	longestStreak: number;
	lastActivityDate: string | null; // ISO 'YYYY-MM-DD'
	streakStatus: StreakStatus;
	/** Streak Freezes currently owned (0–2). */
	freezesOwned: number;
}

interface StoredStreak {
	current: number;
	longest: number;
	lastStudyDate: string | null;
	freezesOwned: number;
}

// ── Date helpers ────────────────────────────────────────────────────────────
// All "day" math runs on the shared learning day from core gamification:
// a new day starts at 5:00 PM UTC, not at midnight. todayISO/yesterdayISO
// return day KEYS ('YYYY-MM-DD' labels), not calendar dates.
function todayISO(): string {
	return streakDayISO();
}

function yesterdayISO(): string {
	// The previous learning day: shift "now" back a full 24 hours.
	return streakDayISO(new Date(Date.now() - 86_400_000));
}

/** Whole days from day-key `a` to the current learning day. */
function daysSinceISO(a: string): number {
	const [ay, am, ad] = a.split('-').map((n) => parseInt(n, 10));
	const aMs = Date.UTC(ay, am - 1, ad);
	const t = todayISO();
	const [ty, tm, td] = t.split('-').map((n) => parseInt(n, 10));
	const tMs = Date.UTC(ty, tm - 1, td);
	return Math.round((tMs - aMs) / 86_400_000);
}

// ── Read / derive ─────────────────────────────────────────────────────────
function readStored(): StoredStreak {
	try {
		const raw = localStorage.getItem(STREAK_KEY);
		if (!raw)
			return {
				current: 0,
				longest: 0,
				lastStudyDate: null,
				freezesOwned: 0,
			};
		const parsed = JSON.parse(raw) as Partial<StoredStreak>;
		return {
			current: parsed.current ?? 0,
			longest: parsed.longest ?? 0,
			lastStudyDate: parsed.lastStudyDate ?? null,
			freezesOwned: parsed.freezesOwned ?? 0,
		};
	} catch {
		return { current: 0, longest: 0, lastStudyDate: null, freezesOwned: 0 };
	}
}

function deriveStatus(s: StoredStreak): StreakStatus {
	if (!s.lastStudyDate || s.current <= 0) return 'none';
	if (s.lastStudyDate === todayISO()) return 'active-today';
	if (s.lastStudyDate === yesterdayISO()) return 'at-risk';
	// Older than yesterday: fully-missed days so far = daysSince - 1 (today is
	// still available to study). Freezes covering every missed day mean the
	// streak is protected, not broken — mirrors core applyStudyToday.
	const missed = daysSinceISO(s.lastStudyDate) - 1;
	if (missed > 0 && missed <= s.freezesOwned) return 'frozen';
	return 'broken';
}

export function getStreakView(): StreakView {
	const s = readStored();
	const status = deriveStatus(s);
	// A broken streak reads as 0 current for display purposes, but the stored
	// number is left intact until the next study day recomputes it (the writer
	// in srs.ts owns that reset via applyStudyToday). A FROZEN streak keeps
	// its number — that's the whole point of the freeze.
	const display = status === 'broken' ? 0 : s.current;
	return {
		currentStreak: display,
		longestStreak: s.longest,
		lastActivityDate: s.lastStudyDate,
		streakStatus: status,
		freezesOwned: s.freezesOwned,
	};
}

// Copy for the popover / nav helper text. Soft "ready, not owed" language —
// no guilt, no sirens.
export const STREAK_MESSAGES: Record<StreakStatus, string> = {
	'active-today': 'Streak protected today',
	'at-risk': 'A quick review today keeps your streak going',
	frozen: 'Your Streak Freeze is holding your streak. A quick review today keeps it going',
	broken: 'Start fresh today. A short review is all it takes',
	none: 'Do one learning action to start a streak',
};

// ── Hook ──────────────────────────────────────────────────────────────────
// Re-reads on cross-tab `storage` events and on the same-tab `nis:progress`
// signal that srs.ts / lessonProgress.ts fire whenever study state changes —
// which is exactly when recordStudyToday() may have updated the streak.
export function useStreak(): StreakView & { message: string } {
	const [view, setView] = useState<StreakView>(getStreakView);

	useEffect(() => {
		const refresh = () => setView(getStreakView());
		window.addEventListener('storage', refresh);
		window.addEventListener('nis:progress', refresh);
		return () => {
			window.removeEventListener('storage', refresh);
			window.removeEventListener('nis:progress', refresh);
		};
	}, []);

	return { ...view, message: STREAK_MESSAGES[view.streakStatus] };
}
